import { useEffect, useMemo, useRef, useState } from "react";
import { toast } from "sonner";
import {
  BarChart3, Ban, Bot, Check, ChevronDown, ChevronRight, Download, FileUp, Link2, FlaskConical, ListChecks, Loader2, Phone, PhoneCall, Play, Plus, ShieldCheck, Sparkles, Trash2, X,
} from "lucide-react";
import { FollowupsView, InsightsView, AnalyticsView } from "./CallTabs";
import ConnectVoiceModal from "./ConnectVoiceModal";
import { useListView, ListBar } from "./ListSearch";
import { FeaturesPanel, VoicePicker } from "./CallFeatures";
import {
  getCallStatus, setupVoiceAgent, getVoicePreview, parseCallFile, importCallContacts, addCallContact,
  getExtractionFields, listCallContacts, deleteCallContact, queueCalls, cancelCall, dialCallNow, getAgentConfig, saveAgentConfig, listCalls, listDnc, addDnc, checkCalling,
  listCallFollowups, setCallScope, callProviders, setTestContact, downloadCallAuditCsv,
} from "../lib/api";

export const OUTCOME_META = {
  demo_booked: { label: "Meeting booked", tone: "green" },
  callback_requested: { label: "Call back", tone: "blue" },
  send_info: { label: "Send info", tone: "blue" },
  not_interested: { label: "Not interested", tone: "neutral" },
  wrong_person: { label: "Wrong person", tone: "yellow" },
  voicemail: { label: "Voicemail", tone: "neutral" },
  do_not_call: { label: "Do not call", tone: "hot" },
  no_conversation: { label: "No answer", tone: "neutral" },
};
const STATUS_META = {
  queued: { label: "Queued", tone: "purple" },
  dialing: { label: "Dialing…", tone: "yellow" },
  in_progress: { label: "On the call", tone: "green" },
  ended: { label: "Ended", tone: "neutral" },
  skipped: { label: "Skipped", tone: "yellow" },
  failed: { label: "Failed", tone: "hot" },
  cancelled: { label: "Cancelled", tone: "neutral" },
};
const FIELDS = [
  { key: "name", label: "Full name", hint: ["full name", "name", "contact"] },
  { key: "first_name", label: "First name", hint: ["first"] },
  { key: "last_name", label: "Last name", hint: ["last", "surname"] },
  { key: "company", label: "Company", hint: ["company", "organization", "organisation", "account", "facility"] },
  { key: "title", label: "Job title", hint: ["title", "role", "position"] },
  { key: "phone", label: "Phone", hint: ["phone", "mobile", "cell", "tel", "number"] },
  { key: "email", label: "Email", hint: ["email", "e-mail"] },
];

function Tag({ tone = "neutral", children }) { return <span className={`badge badge-${tone}`}>{children}</span>; }
const ago = (iso) => iso ? new Date(iso).toLocaleString([], { month: "short", day: "numeric", hour: "numeric", minute: "2-digit" }) : "";

function autoMap(headers) {
  const map = {};
  FIELDS.forEach(f => {
    const found = headers.find(h => f.hint.some(x => h.toLowerCase().includes(x)) && !Object.values(map).includes(h));
    if (found) map[f.key] = found;
  });
  if (map.name && (map.first_name === map.name || map.last_name === map.name)) { delete map.first_name; delete map.last_name; }
  return map;
}

function ImportModal({ close, onDone }) {
  const inputRef = useRef(null);
  const [file, setFile] = useState(null);
  const [parsed, setParsed] = useState(null);
  const [mapping, setMapping] = useState({});
  const [busy, setBusy] = useState(false);

  const pick = async (f) => {
    if (!f) return;
    setFile(f); setBusy(true);
    try {
      const buf = await f.arrayBuffer();
      let bin = ""; new Uint8Array(buf).forEach(b => { bin += String.fromCharCode(b); });
      const data = await parseCallFile(f.name, btoa(bin));
      setParsed(data); setMapping(autoMap(data.headers));
    } catch (e) { toast.error(e?.response?.data?.detail || "Could not read that file"); setFile(null); }
    finally { setBusy(false); }
  };

  const mapped = useMemo(() => (parsed?.rows || []).map(r => Object.fromEntries(FIELDS.map(f => [f.key, mapping[f.key] ? r[mapping[f.key]] : ""]))), [parsed, mapping]);
  const withPhone = mapped.filter(r => String(r.phone || "").trim()).length;

  const doImport = async () => {
    setBusy(true);
    try {
      const res = await importCallContacts(mapped);
      toast.success(`${res.created} added · ${res.updated} updated${res.invalid ? ` · ${res.invalid} skipped (no valid phone)` : ""}`);
      onDone(); close();
    } catch (e) { toast.error(e?.response?.data?.detail || "Import failed"); }
    finally { setBusy(false); }
  };

  return <div className="modal-backdrop" onClick={close}><div className="modal modal-wide" onClick={e => e.stopPropagation()} data-testid="calls-import-modal">
    <button className="modal-close" onClick={close}><X size={18} /></button>
    <div className="modal-icon"><FileUp size={19} /></div>
    <h2>Import a call list</h2>
    <p>Excel (.xlsx) or CSV. Each row needs a phone number; name, company and title make the call personal.</p>
    {!parsed && <div className="dropzone">
      <FileUp size={22} /><b>{file ? file.name : "Choose a spreadsheet"}</b><span>.xlsx or .csv — first sheet, first row is the header</span>
      <input ref={inputRef} type="file" accept=".xlsx,.xlsm,.csv" hidden onChange={e => pick(e.target.files?.[0])} data-testid="calls-file-input" />
      <button className="outline-button" onClick={() => inputRef.current?.click()} disabled={busy}>{busy ? <Loader2 size={14} className="spin" /> : null} Browse</button>
    </div>}
    {parsed && <>
      <div className="call-map-grid">
        {FIELDS.map(f => <label key={f.key}>{f.label}{f.key === "phone" ? " *" : ""}
          <select value={mapping[f.key] || ""} onChange={e => setMapping(m => ({ ...m, [f.key]: e.target.value || undefined }))} data-testid={`calls-map-${f.key}`}>
            <option value="">— not in sheet —</option>
            {parsed.headers.map(h => <option key={h} value={h}>{h}</option>)}
          </select>
        </label>)}
      </div>
      <div className="table-wrap call-preview"><table><thead><tr><th>Name</th><th>Company</th><th>Title</th><th>Phone</th></tr></thead>
        <tbody>{mapped.slice(0, 5).map((r, i) => <tr key={i}><td>{r.name || `${r.first_name || ""} ${r.last_name || ""}`}</td><td>{r.company}</td><td>{r.title}</td><td>{r.phone}</td></tr>)}</tbody></table></div>
      <div className="modal-actions">
        <span className="call-muted">{parsed.rows.length} rows · {withPhone} with a phone number</span>
        <button className="outline-button" onClick={() => { setParsed(null); setFile(null); }}>Choose another file</button>
        <button className="primary-button" disabled={busy || !mapping.phone || withPhone === 0} onClick={doImport} data-testid="calls-import-confirm">{busy ? <Loader2 size={14} className="spin" /> : <Check size={14} />} Import {withPhone}</button>
      </div>
    </>}
  </div></div>;
}

function AddNumberModal({ close, onDone }) {
  const [form, setForm] = useState({ name: "", company: "", title: "", phone: "", email: "" });
  const [busy, setBusy] = useState(false);
  const set = (k) => (e) => setForm(f => ({ ...f, [k]: e.target.value }));
  const save = async () => {
    setBusy(true);
    try { await addCallContact(form); toast.success("Added to the call list"); onDone(); close(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Could not add that number"); }
    finally { setBusy(false); }
  };
  return <div className="modal-backdrop" onClick={close}><div className="modal" onClick={e => e.stopPropagation()} data-testid="calls-add-modal">
    <button className="modal-close" onClick={close}><X size={18} /></button>
    <div className="modal-icon"><Phone size={19} /></div>
    <h2>Add a number</h2>
    <p>Type one contact by hand — for a single prospect, or your own phone to hear a test call.</p>
    <div className="form-panel call-form">
      <label>FULL NAME<input value={form.name} onChange={set("name")} placeholder="Jane Smith" data-testid="calls-add-name" /></label>
      <label>PHONE *<input value={form.phone} onChange={set("phone")} placeholder="+1 555 010 0142" data-testid="calls-add-phone" /></label>
      <div className="two-col">
        <label>COMPANY<input value={form.company} onChange={set("company")} placeholder="Sunrise Senior Living" /></label>
        <label>JOB TITLE<input value={form.title} onChange={set("title")} placeholder="Executive Director" /></label>
      </div>
      <label>EMAIL<input value={form.email} onChange={set("email")} placeholder="optional" /></label>
    </div>
    <button className="primary-button full" disabled={busy || !form.phone.trim()} onClick={save} data-testid="calls-add-save">{busy ? <Loader2 size={14} className="spin" /> : <Plus size={14} />} Add to call list</button>
  </div></div>;
}

function ConfirmModal({ contacts, close, onQueued }) {
  const [consent, setConsent] = useState(false);
  const [busy, setBusy] = useState(false);
  const go = async () => {
    setBusy(true);
    try {
      const res = await queueCalls(contacts.map(c => c.id), consent);
      if (res.queued.length) toast.success(`${res.queued.length} call${res.queued.length === 1 ? "" : "s"} queued — they're dialed one at a time`);
      res.skipped.forEach(s => toast.warning(`${s.name}: ${s.reason}`));
      onQueued(); close();
    } catch (e) { toast.error(e?.response?.data?.detail || "Could not queue calls"); }
    finally { setBusy(false); }
  };
  const later = contacts.filter(c => c.blocker && c.blocker.startsWith("outside calling hours")).length;
  return <div className="modal-backdrop" onClick={close}><div className="modal" onClick={e => e.stopPropagation()} data-testid="calls-confirm-modal">
    <button className="modal-close" onClick={close}><X size={18} /></button>
    <div className="modal-icon"><ShieldCheck size={19} /></div>
    <h2>Call {contacts.length} contact{contacts.length === 1 ? "" : "s"}?</h2>
    <p>Every call opens with the AI agent saying it's an AI assistant calling on your behalf and that the call is recorded. Anyone who asks not to be called is added to the do-not-call list automatically.</p>
    <ul className="call-confirm-list">{contacts.slice(0, 6).map(c => <li key={c.id}><b>{c.name}</b> <span>{c.company} · {c.phone_display}</span></li>)}{contacts.length > 6 && <li><span>+ {contacts.length - 6} more</span></li>}</ul>
    {later > 0 && <div className="call-note">{later} of these are outside calling hours right now — they'll be dialed automatically when their local calling window opens.</div>}
    <label className="call-consent" data-testid="calls-consent-label">
      <input type="checkbox" checked={consent} onChange={e => setConsent(e.target.checked)} data-testid="calls-consent-checkbox" />
      <span>I confirm these are business phone lines, or these people have agreed to be called by us. AI-voice sales calls are regulated in most countries (in the US, calls to mobiles need prior written consent under the TCPA and some state laws); we're responsible for following the rules where we call.</span>
    </label>
    <button className="primary-button full" disabled={!consent || busy} onClick={go} data-testid="calls-queue-confirm">{busy ? <Loader2 size={14} className="spin" /> : <PhoneCall size={14} />} Queue calls</button>
  </div></div>;
}

const AGENT_TEXT = [
  ["agent_name", "Agent name", "Alex"],
  ["voice_id", "Voice ID (from your voice provider)", "Elliot"],
  ["company_description", "What your company does (one or two sentences the agent can say)", "We help small clinics fill cancelled appointment slots."],
  ["call_goal", "Goal of the call", "book a short follow-up meeting"],
  ["voicemail_number", "Callback number for voicemails (blank: say you'll follow up by email)", "+1 555 010 0142"],
];
const AGENT_RULES = [
  ["language", "Call language", "en-US"],
  ["phone_region", "Country for numbers typed without +code", "US"],
  ["default_timezone", "Your time zone (used when a number's own zone is unknown)", "America/New_York"],
  ["area_code", "Preferred caller-ID area code (provider numbers only)", ""],
];
const AGENT_NUMBERS = [
  ["call_window_start", "Calls from (hour, contact's local time)", 0, 23],
  ["call_window_end", "Calls until (hour)", 1, 24],
  ["min_hours_between_calls", "Hours between calls to the same number", 1, 720],
  ["max_attempts", "Most attempts per number", 1, 20],
];

const OWN_DASHBOARD_PROVIDERS = ["bolna", "sarvam", "smallest"];

// For Bolna, Sarvam and Smallest.ai the agent lives in their dashboard: show what it should extract so the CRM can
// read the outcome and create prospects, and say plainly which CRM rules do not apply there.
function ExtractionFields({ provider }) {
  const [fields, setFields] = useState([]);
  useEffect(() => { getExtractionFields().then(r => setFields(r.fields)).catch(() => {}); }, []);
  const copy = () => navigator.clipboard?.writeText(fields.map(f => `${f.name}${f.enum ? ` (one of: ${f.enum.join(", ")})` : ""}: ${f.description}`).join("\n")).then(() => toast.success("Copied"));
  return <fieldset className="key-group" data-testid="extraction-fields">
    <legend>Fields to extract ({provider})</legend>
    <p>This agent is set up in {provider}'s own dashboard, so the script, voice and language are set there, and the AI-and-recording disclosure and calling window above do <b>not</b> apply to its calls. Put the disclosure in the agent's opening line. Add these fields to the agent's end-of-call data extraction, with these exact names:</p>
    <ul className="extract-list">{fields.map(f => <li key={f.name}><code>{f.name}</code>{f.enum ? <small> one of: {f.enum.join(", ")}</small> : null}</li>)}</ul>
    <button className="outline-button" onClick={copy}>Copy the list</button>
  </fieldset>;
}

function AgentConfigModal({ close, onDone, provider }) {
  const [form, setForm] = useState(null);
  const [busy, setBusy] = useState(true);
  useEffect(() => {
    getAgentConfig().then(r => { setForm(r); setBusy(false); }).catch(() => { toast.error("Could not load agent settings"); setBusy(false); });
  }, []);
  const set = (k) => (e) => setForm(f => ({ ...f, [k]: e.target.type === "checkbox" ? e.target.checked : e.target.type === "number" ? Number(e.target.value) : e.target.value }));
  const save = async () => {
    setBusy(true);
    try {
      await saveAgentConfig(form);
      toast.success("Agent settings saved");
      onDone(); close();
    } catch (e) { toast.error(e?.response?.data?.detail || "Could not save agent settings"); }
    finally { setBusy(false); }
  };
  return <div className="modal-backdrop" onClick={close}><div className="modal modal-wide" onClick={e => e.stopPropagation()} data-testid="calls-agent-modal">
    <button className="modal-close" onClick={close}><X size={18} /></button>
    <h2>AI caller settings</h2>
    {OWN_DASHBOARD_PROVIDERS.includes(provider) ? <ExtractionFields provider={provider} /> : <p>What the agent says, and when it's allowed to call. The AI and recording disclosure and the do-not-call list are always on.</p>}
    {form && <div className="form-panel call-form">
      {AGENT_TEXT.map(([k, label, ph]) => <label key={k}>{label}<input value={form[k] || ""} onChange={set(k)} placeholder={ph} data-testid={`agent-${k}`} /></label>)}
      <VoicePicker value={form.voice_id} onChange={v => setForm(f => ({ ...f, voice_id: v }))} />
      <label>Script and talking points<textarea value={form.custom_prompt || ""} onChange={set("custom_prompt")} rows={5}
        placeholder="What to say about your offer, questions to ask, answers to common objections. Never include prices or claims you can't back up." data-testid="agent-custom_prompt" /></label>
      {AGENT_RULES.map(([k, label, ph]) => <label key={k}>{label}<input value={form[k] || ""} onChange={set(k)} placeholder={ph} data-testid={`agent-${k}`} /></label>)}
      <div className="two-col">{AGENT_NUMBERS.map(([k, label, min, max]) => <label key={k}>{label}<input type="number" min={min} max={max} value={form[k] ?? ""} onChange={set(k)} data-testid={`agent-${k}`} /></label>)}</div>
      <label className="call-consent"><input type="checkbox" checked={!!form.weekdays_only} onChange={set("weekdays_only")} /> <span>Weekdays only</span></label>
    </div>}
    <button className="primary-button full" disabled={busy || !form} onClick={save} data-testid="agent-save">{busy ? <Loader2 size={14} className="spin" /> : <Check size={14} />} Save settings</button>
  </div></div>;
}

function WebCallModal({ close, status }) {
  return <div className="modal-backdrop" onClick={close}><div className="modal" onClick={e => e.stopPropagation()} data-testid="calls-web-call-modal">
    <button className="modal-close" onClick={close}><X size={18} /></button>
    <div className="modal-icon"><Phone size={19} /></div>
    <h2>Test Web Call with {status.agent_name}</h2>
    <p>You can talk with {status.agent_name} directly in your browser using your computer's microphone — no phone number needed.</p>
    <div className="form-panel call-form" style={{ padding: "16px", background: "var(--surface-muted)", borderRadius: "8px", margin: "16px 0" }}>
      <p style={{ margin: "0 0 12px 0" }}><b>Assistant ID:</b> <code>{status.assistant_id || "Not created yet (run Setup first)"}</code></p>
      <p style={{ margin: 0 }}>To start an instant voice conversation in your browser:</p>
      <ol style={{ margin: "8px 0 0 20px", padding: 0, lineHeight: 1.6 }}>
        <li>Go to your <a href="https://dashboard.vapi.ai" target="_blank" rel="noreferrer" style={{ color: "var(--primary)", textDecoration: "underline" }}>Vapi Dashboard</a>.</li>
        <li>Open <b>Assistants</b> and select <b>Outbound Caller</b>.</li>
        <li>Click the <b>"Talk"</b> button at the top right to speak with {status.agent_name}.</li>
      </ol>
    </div>
    <a href="https://dashboard.vapi.ai" target="_blank" rel="noreferrer" className="primary-button full" style={{ display: "block", textAlign: "center", textDecoration: "none", lineHeight: "36px" }}>Open Vapi Dashboard to Talk</a>
  </div></div>;
}

function CallDetail({ call }) {
  const d = call.data || {};
  const rows = [["Interest", d.interest_level], ["Sentiment", d.sentiment], ["Main need", d.main_need || d.pain_point], ["Decision maker", d.decision_maker], ["Objections", d.objections], ["Timeline", d.timeline], ["Next step", d.next_step], ["Meeting", d.demo_preference], ["Email confirmed", d.confirmed_email], ["Call back", d.callback_time], ["Referred to", d.referral], ["Notes", d.notes], ["Ended because", call.ended_reason], ["Cost", call.cost != null ? `$${Number(call.cost).toFixed(2)}` : ""], ["Note", call.note]].filter(([, v]) => v);
  return <div className="call-detail">
    {call.summary && <p className="call-summary">{call.summary}</p>}
    {rows.length > 0 && <dl>{rows.map(([k, v]) => <div key={k}><dt>{k}</dt><dd>{v}</dd></div>)}</dl>}
    {call.recording_url && <audio controls src={call.recording_url} preload="none" />}
    {call.transcript && <pre className="call-transcript">{call.transcript}</pre>}
  </div>;
}

function CallsTab() {
  const [status, setStatus] = useState(null);
  const [contacts, setContacts] = useState([]);
  const [calls, setCalls] = useState([]);
  const [dnc, setDnc] = useState([]);
  const [selected, setSelected] = useState(new Set());
  const [modal, setModal] = useState(null);
  const [expanded, setExpanded] = useState(null);
  const [settingUp, setSettingUp] = useState(false);
  const [checks, setChecks] = useState(null);
  const [checking, setChecking] = useState(false);
  const runChecks = async () => {
    setChecking(true);
    try { setChecks(await checkCalling()); } catch (e) { toast.error(e?.response?.data?.detail || "Check failed"); } finally { setChecking(false); }
  };
  const [dncInput, setDncInput] = useState("");
  const audioRef = useRef(null);

  const load = async () => {
    try {
      const [s, c, l, d] = await Promise.all([getCallStatus(), listCallContacts(), listCalls(), listDnc()]);
      setStatus(s); setContacts(c); setCalls(l); setDnc(d);
    } catch (e) { toast.error("Could not load calls"); }
  };
  useEffect(() => { load(); }, []);
  const busyQueue = !!status?.active || (status?.queued || 0) > 0;
  useEffect(() => { const t = setInterval(load, busyQueue ? 5000 : 30000); return () => clearInterval(t); }, [busyQueue]);

  const setup = async () => {
    setSettingUp(true);
    try { const r = await setupVoiceAgent(); toast.success(`Voice agent ready — calling from ${r.phone_number}`); load(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Setup failed"); }
    finally { setSettingUp(false); }
  };
  const preview = async () => {
    try {
      const { preview_url } = await getVoicePreview();
      if (!preview_url) return toast.info("No preview available for this voice");
      audioRef.current?.pause(); audioRef.current = new Audio(preview_url); audioRef.current.play();
    } catch (e) { toast.error("Could not load the voice preview"); }
  };
  const toggle = (id) => setSelected(s => { const n = new Set(s); n.has(id) ? n.delete(id) : n.add(id); return n; });
  const selectable = contacts.filter(c => !c.queue_status && !(c.blocker && !c.blocker.startsWith("outside calling hours")));
  const list = useListView(contacts, c => `${c.name} ${c.company || ""} ${c.title || ""} ${c.email || ""} ${c.phone_display || ""} ${c.phone || ""}`, 8);
  const log = useListView(calls, c => `${c.name} ${c.company || ""} ${c.phone_display || ""} ${c.summary || ""} ${c.outcome || ""} ${c.status || ""}`, 8);
  const dncView = useListView(dnc, d => `${d.name || ""} ${d.phone} ${d.reason || ""}`, 12);
  // "select all" only touches the rows you can see, so nobody is queued from a hidden part of the list
  const visibleSelectable = list.rows.filter(c => selectable.some(s => s.id === c.id));
  const allSelected = visibleSelectable.length > 0 && visibleSelectable.every(c => selected.has(c.id));
  const chosen = contacts.filter(c => selected.has(c.id));

  const markTest = async (c) => {
    try { await setTestContact(c.id, !c.test_contact); toast.success(c.test_contact ? "No longer a test number" : "Marked as your own test number — always callable, never on do-not-call"); load(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Could not change that"); }
  };
  const remove = async (id) => { try { await deleteCallContact(id); setSelected(s => { const n = new Set(s); n.delete(id); return n; }); load(); } catch (e) { toast.error("Could not delete"); } };
  const cancel = async (id) => { try { await cancelCall(id); load(); } catch (e) { toast.error(e?.response?.data?.detail || "Could not cancel"); } };
  const blockNumber = async () => {
    try { await addDnc(dncInput); setDncInput(""); toast.success("Added to do-not-call"); load(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Could not add"); }
  };

  const booked = calls.filter(c => c.outcome === "demo_booked").length;
  const talked = calls.filter(c => c.status === "ended" && !["no_conversation", "voicemail"].includes(c.outcome)).length;

  if (!status) return <div className="page-view"><div className="panel" style={{ padding: 24 }}>Loading…</div></div>;

  return <div className="page-view" data-testid="calls-view">
    <div className="view-toolbar">
      <div><div className="eyebrow"><span className="eyebrow-dot" /> AI VOICE AGENT</div><h2>Calls</h2>
        <p>{status.agent_name} calls your contacts to {status.call_goal || "book a meeting"}. You pick every batch; calls go out one at a time.</p></div>
      <div className="toolbar-actions">
        <button className="outline-button" onClick={() => setModal("connectVoice")} data-testid="calls-connect-agent"><Link2 size={14} /> Connect agent</button>
        <button className="outline-button" onClick={() => setModal("webCall")} data-testid="calls-web-call"><Phone size={14} /> Test in Browser</button>
        <button className="outline-button" onClick={() => setModal("agentSettings")} data-testid="calls-agent-settings"><Sparkles size={14} /> Agent Settings</button>
        <button className="outline-button" onClick={preview} data-testid="calls-voice-preview"><Play size={14} /> Hear {status.agent_name}</button>
        <button className="outline-button" onClick={() => setModal("import")} data-testid="calls-import-button"><FileUp size={14} /> Import Excel / CSV</button>
        <button className="outline-button" onClick={() => setModal("add")} data-testid="calls-add-button"><Plus size={14} /> Add number</button>
      </div>
    </div>

    <div className="panel" style={{ padding: 14, marginBottom: 14 }} data-testid="calls-check-panel">
      <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
        <b style={{ fontSize: 13 }}>Is calling ready?</b>
        <button className="outline-button" onClick={runChecks} disabled={checking} data-testid="calls-check-button">{checking ? <Loader2 size={13} className="spin" /> : <ShieldCheck size={13} />} Check calling setup</button>
        <small className="muted">Checks your keys and phone number. Nothing is dialed and nothing is charged.</small>
      </div>
      {checks && <ul style={{ margin: "10px 0 0", paddingLeft: 0, listStyle: "none", display: "grid", gap: 6 }}>
        {checks.map(c => <li key={c.name} data-testid={`check-${c.name}`} style={{ fontSize: 12 }}>
          {c.ok === true ? "✓" : c.ok === false ? "✗" : "•"} <b>{c.name}</b> — {c.detail}</li>)}
      </ul>}
    </div>
    {!status.configured && <div className="status-banner"><div className="status-icon"><Ban size={16} /></div><div><b>{status.provider_label || "Voice provider"} isn't connected</b><span>Add its key in Settings (you can also pick a different calling provider there).</span><button onClick={() => setModal("connectVoice")}><Link2 size={13} /> Connect agent</button></div></div>}
    {status.configured && !status.ready && <div className="status-banner"><div className="status-icon"><Phone size={16} /></div><div><b>Set up the voice agent</b><span>Gets the AI caller ready with your chosen provider and caller-ID number. Set what it says under Agent Settings first.</span></div>
      <button onClick={setup} disabled={settingUp} data-testid="calls-setup-button">{settingUp ? <Loader2 size={13} className="spin" /> : <ChevronRight size={13} />} Set up now</button></div>}

    {status.ready && <div className="call-strip">
      <div><small>CALLER ID</small><b>{status.phone_number}</b></div>
      <div><small>VOICE</small><b>{status.agent_name} · {status.voice}</b></div>
      <div><small>CALLING HOURS</small><b>{status.call_window}</b></div>
      <div><small>QUEUE</small><b>{status.active ? <span className="call-live"><span className="pulse-dot" /> Calling {status.active.name}</span> : `${status.queued} waiting`}</b></div>
      <div><small>RESULTS</small><b>{booked} meeting{booked === 1 ? "" : "s"} · {talked} conversation{talked === 1 ? "" : "s"}</b></div>
    </div>}

    <div className="panel full-table call-section">
      <div className="call-section-head">
        <div><h3>Call list</h3><small>{contacts.length} contact{contacts.length === 1 ? "" : "s"} · local time shown for each number</small></div>
        <button className="primary-button" disabled={!status.ready || chosen.length === 0} onClick={() => setModal("confirm")} data-testid="calls-call-selected"><PhoneCall size={14} /> Call selected{chosen.length ? ` (${chosen.length})` : ""}</button>
      </div>
      <ListBar view={list} noun="contacts" placeholder="Search name, company, phone, email…" />
      <table>
        <thead><tr>
          <th className="check-col"><input type="checkbox" checked={allSelected} onChange={() => setSelected(s => { const n = new Set(s); visibleSelectable.forEach(c => allSelected ? n.delete(c.id) : n.add(c.id)); return n; })} data-testid="calls-select-all" /></th>
          <th>Contact</th><th>Phone</th><th>Can call?</th><th>Attempts</th><th>Last result</th><th />
        </tr></thead>
        <tbody>
          {contacts.length === 0 && <tr><td colSpan={7} className="empty-row"><Phone size={16} /> No contacts yet — import an Excel sheet or add a number.</td></tr>}
          {contacts.length > 0 && list.rows.length === 0 && <tr><td colSpan={7} className="empty-row">No contacts match that search.</td></tr>}
          {list.rows.map(c => {
            const canPick = selectable.some(s => s.id === c.id);
            const out = c.last_outcome && OUTCOME_META[c.last_outcome];
            return <tr key={c.id} data-testid={`calls-contact-${c.id}`}>
              <td><input type="checkbox" disabled={!canPick} checked={selected.has(c.id)} onChange={() => toggle(c.id)} /></td>
              <td><div className="person-cell"><div className="avatar avatar-blue">{(c.name || "?").split(" ").map(w => w[0]).slice(0, 2).join("").toUpperCase()}</div><div><b>{c.name}{c.test_contact && <span className="badge badge-purple test-tag">Test</span>}</b><small>{[c.title, c.company].filter(Boolean).join(" · ")}</small></div></div></td>
              <td><div className="call-phone"><b>{c.phone_display}</b><small>{c.local_time}</small></div></td>
              <td>{c.queue_status ? <Tag tone={STATUS_META[c.queue_status].tone}>{STATUS_META[c.queue_status].label}</Tag>
                : c.callable_now ? <Tag tone="green">Yes</Tag>
                : <small className="call-muted">{c.blocker}</small>}</td>
              <td>{c.attempts}/{status.max_attempts}</td>
              <td>{out ? <Tag tone={out.tone}>{out.label}</Tag> : <small className="call-muted">—</small>}</td>
              <td><div style={{ display: "flex", gap: 6 }}>
                <button className="icon-button" title={c.test_contact ? "Stop treating as a test number" : "This is my own phone (test number)"} onClick={() => markTest(c)} data-testid={`calls-test-${c.id}`}><FlaskConical size={14} /></button>
                <button className="icon-button" title="Remove" onClick={() => remove(c.id)} data-testid={`calls-remove-${c.id}`}><Trash2 size={14} /></button></div></td>
            </tr>;
          })}
        </tbody>
      </table>
    </div>

    <div className="panel full-table call-section">
      <div className="call-section-head"><div><h3>Call log</h3><small>Outcome, summary, transcript and recording for every call</small></div>
        <button className="outline-button" onClick={() => downloadCallAuditCsv().catch(() => toast.error("Download failed"))} data-testid="calls-audit-csv"><Download size={14} /> Audit log (CSV)</button></div>
      <ListBar view={log} noun="calls" placeholder="Search name, company, number, outcome…" />
      <table>
        <thead><tr><th /><th>When</th><th>Contact</th><th>Status</th><th>Outcome</th><th>Summary</th><th /></tr></thead>
        <tbody>
          {calls.length === 0 && <tr><td colSpan={7} className="empty-row"><PhoneCall size={16} /> No calls yet.</td></tr>}
          {log.rows.map(c => {
            const st = STATUS_META[c.status] || { label: c.status, tone: "neutral" };
            const out = c.outcome && OUTCOME_META[c.outcome];
            const open = expanded === c.id;
            return [<tr key={c.id} className="call-row" onClick={() => setExpanded(open ? null : c.id)} data-testid={`calls-log-${c.id}`}>
              <td>{open ? <ChevronDown size={14} /> : <ChevronRight size={14} />}</td>
              <td><small>{ago(c.dialed_at || c.queued_at)}</small></td>
              <td><b>{c.name}</b><br /><small className="call-muted">{c.company} · {c.phone_display}</small></td>
              <td><Tag tone={st.tone}>{st.label}</Tag>{c.waiting_for_hours && c.status === "queued" && <small className="call-muted"> waits for calling hours</small>}</td>
              <td>{out ? <Tag tone={out.tone}>{out.label}</Tag> : ""}</td>
              <td className="call-summary-cell"><small>{c.summary || c.note || ""}</small></td>
              <td>{c.status === "queued" && <div style={{ display: "flex", gap: "6px" }}><button className="outline-button" onClick={async (e) => { e.stopPropagation(); try { await dialCallNow(c.id); toast.success("Dialing call now..."); load(); } catch (err) { toast.error(err?.response?.data?.detail || "Could not dial"); } }}>Dial now</button><button className="outline-button" onClick={(e) => { e.stopPropagation(); cancel(c.id); }}>Cancel</button></div>}</td>
            </tr>, open && <tr key={`${c.id}-d`}><td /><td colSpan={6}><CallDetail call={c} /></td></tr>];
          })}
        </tbody>
      </table>
    </div>

    <div className="panel call-section call-dnc">
      <div><h3>Do-not-call list</h3><small>{dnc.length} number{dnc.length === 1 ? "" : "s"} — never dialed. People who ask on a call are added automatically.</small></div>
      <div className="call-dnc-add"><input value={dncInput} onChange={e => setDncInput(e.target.value)} placeholder="Add a number" data-testid="calls-dnc-input" /><button className="outline-button" disabled={!dncInput.trim()} onClick={blockNumber}><Ban size={14} /> Block</button></div>
      {dnc.length > 12 && <ListBar view={dncView} noun="numbers" placeholder="Search a number or name…" />}
      {dnc.length > 0 && <div className="call-dnc-list">{dncView.rows.map(d => <span key={d.phone} title={d.reason}>{d.name ? `${d.name} · ` : ""}{d.phone}</span>)}</div>}
    </div>

    {modal === "import" && <ImportModal close={() => setModal(null)} onDone={load} />}
    {modal === "add" && <AddNumberModal close={() => setModal(null)} onDone={load} />}
    {modal === "confirm" && <ConfirmModal contacts={chosen} close={() => setModal(null)} onQueued={() => { setSelected(new Set()); load(); }} />}
    {modal === "agentSettings" && <AgentConfigModal close={() => setModal(null)} onDone={load} provider={status.provider} />}
    {modal === "connectVoice" && <ConnectVoiceModal close={() => setModal(null)} onConnected={load} />}
    {modal === "webCall" && <WebCallModal close={() => setModal(null)} status={status} />}
  </div>;
}

function AgentTab() {
  const [status, setStatus] = useState(null);
  const [open, setOpen] = useState(false);
  useEffect(() => { getCallStatus().then(setStatus).catch(() => {}); }, [open]);
  return <div className="page-view" data-testid="agent-view">
    <div className="view-toolbar">
      <div><h2>Agent</h2><p>Switch on live transfer and calendar booking, and change who the agent is, how it sounds, and what it says. Saving updates the live agent straight away.</p></div>
      <div className="toolbar-actions"><button className="primary-button" onClick={() => setOpen(true)} data-testid="agent-open-settings"><Sparkles size={14} /> Name, voice, script and calling hours</button></div>
    </div>
    {status && !["vapi"].includes(status.provider) && <div className="panel" style={{ marginBottom: 14 }}><p>Live transfer and calendar booking work with Vapi. {status.provider_label} agents are set up in their own dashboard.</p></div>}
    <FeaturesPanel />
    {open && status && <AgentConfigModal close={() => setOpen(false)} onDone={() => {}} provider={status.provider} />}
  </div>;
}

const TABS = [
  ["calls", "Calls", PhoneCall, "Queue, dial, results"],
  ["followups", "Follow-ups", ListChecks, "Who to contact back"],
  ["insights", "Call insights", Sparkles, "Who's interested"],
  ["analytics", "Analytics", BarChart3, "Performance and spend"],
  ["agent", "Agent", Bot, "Voice, script, features"],
];

export default function CallsView() {
  const [tab, setTab] = useState("calls");
  const [followups, setFollowups] = useState(0);
  const [scope, setScopeState] = useState("");          // "" = the provider in use, "all", or a provider id
  const [provs, setProvs] = useState(null);
  useEffect(() => { callProviders().then(setProvs).catch(() => {}); }, [tab]);
  setCallScope(scope);                                   // set during render so the tabs below fetch with it
  useEffect(() => {
    const load = () => listCallFollowups(false).then(r => setFollowups(r.length)).catch(() => {});
    load();
    const t = setInterval(load, 30000);
    window.addEventListener("call-followups-changed", load);
    return () => { clearInterval(t); window.removeEventListener("call-followups-changed", load); };
  }, [tab, scope]);
  const shown = scope || provs?.active || "";
  return <div data-testid="calls-module">
    <nav className="call-tabs" aria-label="Calls sections">
      {TABS.map(([id, label, Icon, hint]) => <button key={id} className={`call-tab ${tab === id ? "on" : ""}`} onClick={() => setTab(id)} data-testid={`calls-tab-${id}`} aria-current={tab === id ? "page" : undefined}>
        <span className="tab-ico"><Icon size={17} />{id === "followups" && followups > 0 && <span className="tab-n">{followups}</span>}</span>
        <span><b>{label}</b><small>{hint}</small></span></button>)}
    </nav>
    {provs && provs.providers.length > 1 && tab !== "agent" && <div className="provider-tabs" data-testid="calls-scope">
      <small className="muted">Showing calls from</small>
      {provs.providers.map(p => <button key={p.id} className={shown === p.id ? "primary-button" : "outline-button"} onClick={() => setScopeState(p.active ? "" : p.id)}>{p.label}{p.active ? " · in use" : ""} <small>{p.calls}</small></button>)}
      <button className={shown === "all" ? "primary-button" : "outline-button"} onClick={() => setScopeState("all")}>All providers</button>
    </div>}
    {tab === "calls" && <CallsTab key={shown} />}
    {tab === "followups" && <FollowupsView key={shown} />}
    {tab === "insights" && <InsightsView key={shown} />}
    {tab === "analytics" && <AnalyticsView key={shown} />}
    {tab === "agent" && <AgentTab />}
  </div>;
}
